import { Text } from '@chakra-ui/react'
import { capitalize, flatMap, groupBy, max, min } from 'lodash-es'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { capitalizeWords, isNotNull, wordWithArticle } from '@/utils/helpers'

import SeriesQ from '@/queries/SeriesQ'
import SeriesRegistrationQ from '@/queries/SeriesRegistrationQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import type { ProgramSlug } from '@/client/services/programs/constants'
import { arePrePostSurveysPausedForProgram } from '@/client/services/programs/helpers'
import type { ProgramDetails, ProgramIteration } from '@/client/services/programs/types'
import { getDashboardProgramsUrl, getPersonalizedSurveyUrl } from '@/client/services/url'
import { getUserLevelInWorld, isLevelAtLeastMinLevel } from '@/client/services/worlds'
import { COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG } from '@/client/services/worlds/constants'

import useProgramIterationOpenForRegistration from '@/components/Programs/hooks/useProgramIterationOpenForRegistration'

import { Priority, type TodoItem } from '../../types'
import buildSurveyTodoItems from '../buildSurveyTodoItems'
import type { SurveyTodoDetails, TodosSupportedProgramSlug } from './types'

const DAYS_TO_INCLUDE_SERIES_AFTER_END = 7
const SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END = 5
const POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END = 7

type ProgramSeriesRegistrationDetails = {
  seriesId: number
  startDate: DateTime
  endDate: DateTime
  completedPreSeriesSurvey: boolean
  completedPostSeriesSurvey: boolean
  seriesTemplateSlug: string
  programIteration: ProgramIteration
}

function getSurveyTodosForIteration({
  iteration,
  seriesRegistrationDetails,
  programType,
  programSlug,
  uuidTraining,
  worldSlug,
  now,
}: {
  iteration: ProgramIteration
  seriesRegistrationDetails: ProgramSeriesRegistrationDetails[]
  programType: ProgramDetails['programType']
  programSlug: ProgramSlug
  uuidTraining: string
  worldSlug: string
  now: DateTime
}): SurveyTodoDetails[] {
  if (arePrePostSurveysPausedForProgram(programSlug)) {
    return []
  }

  const completedLearnerPreSeriesSurvey = seriesRegistrationDetails
    .map(registration => registration.completedPreSeriesSurvey)
    .includes(true)

  const completedLearnerPostSeriesSurvey = seriesRegistrationDetails
    .map(registration => registration.completedPostSeriesSurvey)
    .includes(true)

  const minSeriesStartDate = min(seriesRegistrationDetails.map(series => series.startDate))

  const showPostSeriesSurveyAfter = max(
    seriesRegistrationDetails.map(details =>
      details.endDate.minus({ days: SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END })
    )
  )
  const showPostSeriesSurvey = showPostSeriesSurveyAfter != null && now > showPostSeriesSurveyAfter

  const postSeriesSurveyDeadline = max(
    seriesRegistrationDetails.map(details =>
      details.endDate.plus({ days: POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END })
    )
  )

  const preSurveyUrl = iteration.learnerPreSeriesSurveyUrl
  const preSurveyTodo: SurveyTodoDetails | null =
    preSurveyUrl != null
      ? {
          title: `Complete the ${iteration.title} Pre-Survey`,
          subtitle: 'To help us improve the program!',
          deadline: minSeriesStartDate,
          completed: completedLearnerPreSeriesSurvey,
          surveyUrl: preSurveyUrl,
          modalDetails: {
            key: `${programSlug}-${iteration.iterationSlug}-pre-survey`,
            title: `${iteration.title} ${capitalizeWords(programType)} Pre-Survey`,
            body: () => (
              <Text>
                Fill out this 1 minute pre-survey for the {iteration.title}{' '}
                {capitalizeWords(programType)}
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: preSurveyUrl,
              uuidTraining,
              worldSlug,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: seriesRegistrationDetails[0].seriesId,
                surveyType: 'learner-pre-survey',
              }),
          },
        }
      : null

  const postSurveyUrl = iteration.learnerPostSeriesSurveyUrl
  const postSurveyTodo: SurveyTodoDetails | null =
    postSurveyUrl != null && showPostSeriesSurvey
      ? {
          title: `Complete the ${iteration.title} Post-Survey`,
          subtitle: 'To help us improve the program!',
          deadline: postSeriesSurveyDeadline,
          completed: completedLearnerPostSeriesSurvey,
          surveyUrl: postSurveyUrl,
          modalDetails: {
            key: `${programSlug}-${iteration.iterationSlug}-post-survey`,
            title: `${iteration.title} ${capitalize(programType)} Post-Survey`,
            body: () => (
              <Text>
                Fill out this quick post-survey for the {iteration.title}{' '}
                {capitalizeWords(programType)}
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: postSurveyUrl,
              uuidTraining,
              worldSlug,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: seriesRegistrationDetails[0].seriesId,
                surveyType: 'learner-post-survey',
              }),
          },
        }
      : null

  return [preSurveyTodo, postSurveyTodo].filter(isNotNull)
}

export function useProgramLearnerTodos({
  worldSlug,
  programSlug,
  programType,
  iterations,
}: {
  worldSlug: string
  programSlug: TodosSupportedProgramSlug
  programType: ProgramDetails['programType']
  iterations: ProgramIteration[]
}): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const now = useServerNow()
  const [openModalKey, setOpenModalKey] = useState<string | null>(null)

  const programOpenIterationForLearners = useProgramIterationOpenForRegistration({
    programSlug,
    type: 'learner',
  })

  const level = getUserLevelInWorld({ user, worldSlug })

  if (level == null) {
    return []
  }

  const registeredSeriesDetails: ProgramSeriesRegistrationDetails[] = query
    .many(
      SeriesRegistrationQ.where({
        userId: user.id,
        include: [
          {
            association: 'Series',
            where: SQL`${SeriesQ.isActiveOrEndedWithinDays(DAYS_TO_INCLUDE_SERIES_AFTER_END)}`,
            include: [
              {
                association: 'World',
                where: SQL`${WorldQ.slug} = ${worldSlug}`,
                required: true,
              },
              {
                association: 'SeriesTemplate',
                required: false,
              },
            ],
          },
        ],
      })
    )
    .map(series => {
      const seriesTemplateSlug = series(SeriesTemplateQ.slug)

      const relevantIteration = iterations.find(iteration =>
        iteration.offerings.some(offering => offering.seriesTemplateSlug === seriesTemplateSlug)
      )

      return {
        seriesId: series(SeriesQ.id) ?? 0,
        startDate: DateTime.fromISO(`${series(SeriesQ.getComputedStartDate())}`),
        endDate: DateTime.fromISO(`${series(SeriesQ.getComputedEndDate())}`),
        completedPreSeriesSurvey:
          series(SeriesRegistrationQ.hasCompletedPreSeriesSurvey()) ?? false,
        completedPostSeriesSurvey:
          series(SeriesRegistrationQ.hasCompletedPostSeriesSurvey()) ?? false,
        seriesTemplateSlug,
        programIteration: relevantIteration,
      }
    })
    .filter(
      (registration): registration is ProgramSeriesRegistrationDetails =>
        registration.programIteration != null
    )

  const worldMember = query.one(
    WorldMemberQ.where({
      userId: user.id,
      include: [
        {
          association: 'World',
          required: true,
          where: SQL`${WorldQ.slug} = ${worldSlug}`,
        },
      ],
    })
  )
  const expressedIntentToTutor = worldMember(WorldMemberQ.expressedIntentToTutor()) ?? false
  const isTutorInWorld = isLevelAtLeastMinLevel({ level, minLevel: 'tutor' })

  const seriesRegistrationDetailsByIteration = groupBy(
    registeredSeriesDetails,
    details => details.programIteration.iterationSlug
  )

  const surveyTodoDetails = flatMap(
    Object.entries(seriesRegistrationDetailsByIteration),
    ([iterationSlug, seriesRegistrationDetails]) => {
      const iteration = iterations.find(i => i.iterationSlug === iterationSlug)
      if (iteration == null) {
        return []
      }
      return getSurveyTodosForIteration({
        iteration,
        seriesRegistrationDetails,
        programType,
        programSlug,
        uuidTraining: user.uuidTraining,
        worldSlug,
        now,
      })
    }
  )

  const surveyTodos = buildSurveyTodoItems({
    configs: surveyTodoDetails,
    worldSlug,
    targetLevel: 'member',
    openModalKey,
    setOpenModalKey,
  })

  const upcomingProgramIterationSlug = programOpenIterationForLearners?.iterationSlug

  const registeredForUpcomingProgramSeries =
    upcomingProgramIterationSlug != null &&
    (seriesRegistrationDetailsByIteration[upcomingProgramIterationSlug] ?? []).length > 0

  const hideRegisterForSeriesTodo = worldSlug === COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG
  const registerForSeriesTodo: TodoItem | null =
    programOpenIterationForLearners != null && !hideRegisterForSeriesTodo
      ? {
          worldSlug,
          title: `Register for ${wordWithArticle(
            programOpenIterationForLearners.title
          )} ${capitalizeWords(programType)}!`,
          subtitle: `To participate in the ${capitalizeWords(programType)}!`,
          completed: registeredForUpcomingProgramSeries,
          link: getDashboardProgramsUrl({ programSlug }),
          deadline: programOpenIterationForLearners.learnerRegistrationDeadline,
          priority: Priority.High,
          emoji: '📖',
          targetLevel: 'member',
        }
      : null

  if (expressedIntentToTutor || isTutorInWorld) {
    return []
  }

  const todos = [registerForSeriesTodo, ...surveyTodos]
    .filter(isNotNull)
    .filter(todo => todo.deadline == null || now < todo.deadline)

  return todos
}
